/**
 * Sistema di ordinamento per il FilterHandler
 *
 * @description
 * La classe Order gestisce l'ordinamento multiplo per le query OData:
 * - Supporto per ordinamento su più colonne
 * - Gestione dinamica di ascending/descending/ignore
 * - Integrazione con componenti UI (tabelle sortable)
 * - Serializzazione in formato OData orderBy
 */

import { OrderOperator } from '../types/operators';
import type { OrderOperatorType } from '../types/operators';
import type { QueryParams, ODataContext, OrderInfo } from '../types';

/**
 * Interfaccia per dati di ordinamento
 * Utilizzabile per Vue 3 Composition API con oggetti plain reattivi
 */
export interface IOrderData {
	/** Array degli ordinamenti attivi */
	orders: OrderInfo[];
}

/**
 * Tipo combinato per ordinamento con metodi fluenti
 */
export type OrderFactory = IOrderData & {
	/** Aggiunge un ordinamento primario */
	by(propertyName: string, operator?: OrderOperatorType): OrderFactory;
	/** Aggiunge un ordinamento secondario */
	thenBy(propertyName: string, operator?: OrderOperatorType): OrderFactory;
	/** Cambia l'ordinamento per una proprietà */
	changeOrder(propertyName: string, direction: string): void;
	/** Rimuove tutti gli ordinamenti */
	clear(): OrderFactory;
	/** Rimuove un ordinamento specifico */
	remove(propertyName: string): OrderFactory;
	/** Formatta come testo descrittivo */
	formatAsText(): string;
	/** Verifica se ha ordinamenti attivi */
	hasOrders(): boolean;
};

/**
 * Classe per la gestione dell'ordinamento multiplo
 *
 * @description
 * Order fornisce un'API fluente per definire criteri di ordinamento complessi.
 * Supporta ordinamento su più colonne, toggle dinamico degli stati e
 * integrazione seamless con tabelle UI.
 *
 * @example
 * // Ordinamento semplice
 * const order = new Order()
 *   .by('name', OrderOperator.ASCENDING);
 *
 * @example
 * // Ordinamento multiplo
 * const order = new Order()
 *   .by('priority', OrderOperator.DESCENDING)
 *   .thenBy('createdAt', OrderOperator.ASCENDING)
 *   .thenBy('name');  // Default: ASCENDING
 *
 * @example
 * // Uso con tabelle Vue/Element
 * <el-table @sort-change="onSortChange">
 *   <el-table-column prop="name" sortable />
 * </el-table>
 *
 * // Nel component:
 * onSortChange(propertyName, direction) {
 *   this.dataOrder.changeOrder(propertyName, direction);
 *   this.search();
 * }
 *
 * @category Core
 * @since 1.0.0
 */
export class Order {
	/**
	 * Array degli ordinamenti attivi
	 * @private
	 */
	private _orders: OrderInfo[] = [];

	/**
	 * Crea una nuova istanza di Order
	 *
	 * @example
	 * const order = new Order();
	 *
	 * @public
	 */
	constructor() {
		this._orders = [];
	}

	/**
	 * Restituisce una descrizione testuale degli ordinamenti correnti
	 * Utile per mostrare all'utente i criteri di ordinamento attivi
	 *
	 * @returns Stringa descrittiva dell'ordinamento
	 *
	 * @example
	 * const order = new Order()
	 *   .by('priority', OrderOperator.DESCENDING)
	 *   .thenBy('name', OrderOperator.ASCENDING);
	 *
	 * order.formatAsText();
	 * // "priority (desc), name (asc)"
	 *
	 * @example
	 * const emptyOrder = new Order();
	 * emptyOrder.formatAsText(); // ""
	 *
	 * @public
	 */
	public formatAsText(): string {
		const orderings = this._orders
			.filter((order) => order.label && order.operator !== OrderOperator.IGNORE)
			.map((order) => {
				const operator = order.operator === OrderOperator.ASCENDING ? 'asc' : 'desc';
				return order.label ? `${order.label} (${operator})` : '';
			})
			.filter((text) => text !== '');

		return orderings.length > 0 ? orderings.join(', ') : '';
	}

	/**
	 * Imposta il primo criterio di ordinamento
	 * Rimuove eventuali ordinamenti precedenti per la stessa proprietà
	 *
	 * @param propertyName - Nome della proprietà su cui ordinare (supporta notazione con punto)
	 * @param operator - Operatore di ordinamento (default: ASCENDING)
	 * @param label - Etichetta leggibile per il display (opzionale)
	 * @returns L'istanza corrente per method chaining
	 *
	 * @throws {TypeError} Se propertyName non è valido
	 * @throws {TypeError} Se operator non è valido
	 *
	 * @example
	 * // Ordinamento semplice
	 * const order = new Order().by('name');
	 *
	 * @example
	 * // Con operatore specifico
	 * const order = new Order().by('createdAt', OrderOperator.DESCENDING);
	 *
	 * @example
	 * // Con etichetta personalizzata
	 * const order = new Order().by('customer.name', OrderOperator.ASCENDING, 'Cliente');
	 *
	 * @public
	 */
	public by(
		propertyName: string,
		operator: OrderOperatorType = OrderOperator.ASCENDING,
		label?: string,
	): this {
		if (!propertyName || typeof propertyName !== 'string' || propertyName === '') {
			throw new TypeError('Parameter must be in a valid format');
		}

		if (!operator || typeof operator !== 'symbol') {
			throw new TypeError('Parameter is not a valid OrderOperator!');
		}

		const sanitizedPropertyName = propertyName.replace(/\./g, '/');

		// Rimuove eventuali ordinamenti precedenti per la stessa proprietà
		this._orders = this._orders.filter((order) => order.propertyName !== sanitizedPropertyName);

		// Aggiunge il nuovo ordinamento
		this._orders.push({
			propertyName: sanitizedPropertyName,
			operator,
			label: label || propertyName,
		});

		this._clean();
		return this;
	}

	/**
	 * Aggiunge un criterio di ordinamento secondario
	 * Alias per il metodo by(), usato per chiarezza nella lettura del codice
	 *
	 * @param propertyName - Nome della proprietà su cui ordinare
	 * @param operator - Operatore di ordinamento (default: ASCENDING)
	 * @param label - Etichetta leggibile per il display (opzionale)
	 * @returns L'istanza corrente per method chaining
	 *
	 * @example
	 * const order = new Order()
	 *   .by('priority', OrderOperator.DESCENDING)
	 *   .thenBy('name', OrderOperator.ASCENDING)
	 *   .thenBy('createdAt'); // Default ASCENDING
	 *
	 * @public
	 */
	public thenBy(
		propertyName: string,
		operator: OrderOperatorType = OrderOperator.ASCENDING,
		label?: string,
	): this {
		return this.by(propertyName, operator, label);
	}

	/**
	 * Ottiene le informazioni di ordinamento per una proprietà specifica
	 *
	 * @param propertyName - Nome della proprietà da cercare
	 * @returns Informazioni dell'ordinamento o undefined se non trovato
	 *
	 * @throws {TypeError} Se propertyName non è valido
	 *
	 * @example
	 * const order = new Order().by('name', OrderOperator.ASCENDING);
	 * const orderInfo = order.getOrder('name');
	 * // orderInfo: { propertyName: 'name', operator: OrderOperator.ASCENDING, label: 'name' }
	 *
	 * @example
	 * const notFound = order.getOrder('nonexistent');
	 * // notFound: undefined
	 *
	 * @public
	 */
	public getOrder(propertyName: string): OrderInfo | undefined {
		if (!propertyName || typeof propertyName !== 'string' || propertyName === '') {
			throw new TypeError('Parameter must be in a valid format');
		}

		const sanitizedPropertyName = propertyName.replace(/\./g, '/');
		return this._orders.find((item) => item.propertyName === sanitizedPropertyName);
	}

	/**
	 * Cambia l'ordinamento per una proprietà specifica
	 * Se la proprietà non esiste, crea un nuovo ordinamento
	 *
	 * @param propertyName - Nome della proprietà da modificare
	 * @param operator - Nuovo operatore di ordinamento
	 * @param label - Etichetta leggibile per il display (opzionale)
	 *
	 * @throws {TypeError} Se operator non è valido
	 *
	 * @example
	 * const order = new Order().by('name', OrderOperator.ASCENDING);
	 * order.changeOrder('name', OrderOperator.DESCENDING);
	 * // Cambia l'ordinamento di 'name' da ASC a DESC
	 *
	 * @example
	 * order.changeOrder('newField', OrderOperator.ASCENDING);
	 * // Crea un nuovo ordinamento per 'newField'
	 *
	 * @public
	 */
	public changeOrder(propertyName: string, operator: OrderOperatorType, label?: string): void {
		if (!operator || typeof operator !== 'symbol') {
			throw new TypeError('Parameter is not a valid OrderOperator!');
		}

		const sanitizedPropertyName = propertyName.replace(/\./g, '/');
		const existingOrder = this.getOrder(sanitizedPropertyName);

		if (!existingOrder) {
			// Crea un nuovo ordinamento
			this.by(propertyName, operator, label);
		} else {
			// Modifica l'ordinamento esistente
			existingOrder.operator = operator;
			if (label) {
				existingOrder.label = label;
			}
			this._clean();
		}
	}

	/**
	 * Alterna l'ordinamento per una proprietà specifica
	 * Cicla tra: ASCENDING → DESCENDING → IGNORE → ASCENDING
	 *
	 * @param propertyName - Nome della proprietà da alternare
	 * @param label - Etichetta leggibile per il display (opzionale)
	 *
	 * @example
	 * const order = new Order();
	 *
	 * order.toggleOrder('name');
	 * // Primo toggle: ASCENDING
	 *
	 * order.toggleOrder('name');
	 * // Secondo toggle: DESCENDING
	 *
	 * order.toggleOrder('name');
	 * // Terzo toggle: IGNORE (rimuove l'ordinamento)
	 *
	 * order.toggleOrder('name');
	 * // Quarto toggle: torna ad ASCENDING
	 *
	 * @public
	 */
	public toggleOrder(propertyName: string, label?: string): void {
		const sanitizedPropertyName = propertyName.replace(/\./g, '/');
		const existingOrder = this.getOrder(sanitizedPropertyName);

		if (!existingOrder) {
			// Se non esiste, inizia con ASCENDING
			this.by(propertyName, OrderOperator.ASCENDING, label);
		} else {
			// Cicla attraverso gli stati
			switch (existingOrder.operator) {
				case OrderOperator.ASCENDING:
					existingOrder.operator = OrderOperator.DESCENDING;
					break;
				case OrderOperator.DESCENDING:
					existingOrder.operator = OrderOperator.IGNORE;
					break;
				case OrderOperator.IGNORE:
				default:
					existingOrder.operator = OrderOperator.ASCENDING;
					break;
			}
		}

		this._clean();
	}

	/**
	 * Rimuove gli ordinamenti con operatore IGNORE
	 * Mantiene l'array pulito rimuovendo gli ordinamenti inattivi
	 *
	 * @private
	 */
	private _clean(): void {
		this._orders = this._orders.filter((order) => order.operator !== OrderOperator.IGNORE);
	}

	/**
	 * Scrive i parametri di ordinamento nella query string
	 * Serializza gli ordinamenti nel formato OData orderBy
	 *
	 * @param query - Oggetto query string da modificare
	 *
	 * @example
	 * const order = new Order()
	 *   .by('priority', OrderOperator.DESCENDING)
	 *   .thenBy('name', OrderOperator.ASCENDING);
	 *
	 * const query = {};
	 * order._toQuery(query);
	 * // query.orderBy = "priority desc,name asc"
	 *
	 * @protected
	 */
	public _toQuery(query: QueryParams): void {
		const tokens: string[] = [];

		this._orders.forEach((order) => {
			if (order.operator === OrderOperator.IGNORE) {
				return;
			}

			const direction = order.operator === OrderOperator.ASCENDING ? 'asc' : 'desc';
			tokens.push(`${order.propertyName} ${direction}`);
		});

		if (tokens.length > 0) {
			query.orderBy = tokens.join(',');
		}
	}

	/**
	 * Legge i parametri di ordinamento dalla query string
	 * Deserializza dal formato OData orderBy
	 *
	 * @param query - Parametri della query string
	 *
	 * @example
	 * // URL: ?orderBy=priority%20desc,name%20asc
	 * const order = new Order();
	 * order._fromQuery({ orderBy: 'priority desc,name asc' });
	 * // Ricrea gli ordinamenti: priority DESC, name ASC
	 *
	 * @protected
	 */
	public _fromQuery(query: QueryParams): void {
		if (!query.orderBy) {
			return;
		}

		this._orders = [];
		const orderByString = String(query.orderBy);
		const tokens = orderByString.split(',');

		tokens.forEach((item) => {
			const parts = item.trim().split(' ');
			if (parts.length >= 2) {
				const propertyName = parts[0];
				const direction = parts[1].toLowerCase();
				const operator = direction === 'asc' ? OrderOperator.ASCENDING : OrderOperator.DESCENDING;

				this._orders.push({
					propertyName,
					operator,
					label: propertyName,
				});
			}
		});
	}

	/**
	 * Aggiunge i parametri di ordinamento al contesto OData
	 * Imposta l'array orderBy nel contesto della query
	 *
	 * @param context - Contesto OData da modificare
	 *
	 * @example
	 * const context = {};
	 * const order = new Order().by('name', OrderOperator.ASCENDING);
	 * order._setContext(context);
	 * // context.orderBy = ["name asc"]
	 *
	 * @protected
	 */
	public _setContext(context: ODataContext): void {
		const activeOrders = this._orders.filter((order) => order.operator !== OrderOperator.IGNORE);

		if (activeOrders.length > 0) {
			context.orderBy = [];
			activeOrders.forEach((order) => {
				const direction = order.operator === OrderOperator.ASCENDING ? 'asc' : 'desc';
				context.orderBy!.push(`${order.propertyName} ${direction}`);
			});
		}
	}

	/**
	 * Rimuove tutti gli ordinamenti
	 * Resetta l'istanza allo stato iniziale
	 *
	 * @example
	 * const order = new Order().by('name').thenBy('date');
	 * order.clear();
	 * // order._orders = []
	 *
	 * @public
	 */
	public clear(): void {
		this._orders = [];
	}

	/**
	 * Restituisce il numero di ordinamenti attivi
	 *
	 * @returns Numero di ordinamenti attivi (esclusi ququelli con IGNORE)
	 *
	 * @example
	 * const order = new Order()
	 *   .by('name', OrderOperator.ASCENDING)
	 *   .thenBy('date', OrderOperator.IGNORE);
	 *
	 * order.count(); // 1 (solo 'name' è attivo)
	 *
	 * @public
	 */
	public count(): number {
		return this._orders.filter((order) => order.operator !== OrderOperator.IGNORE).length;
	}

	/**
	 * Verifica se ci sono ordinamenti attivi
	 *
	 * @returns true se ci sono ordinamenti attivi, false altrimenti
	 *
	 * @example
	 * const order = new Order();
	 * order.isEmpty(); // true
	 *
	 * order.by('name');
	 * order.isEmpty(); // false
	 *
	 * @public
	 */
	public isEmpty(): boolean {
		return this.count() === 0;
	}

	/**
	 * Restituisce una copia degli ordinamenti attivi
	 * Utile per debugging o per ispezionare lo stato corrente
	 *
	 * @returns Array di copia degli ordinamenti attivi
	 *
	 * @example
	 * const order = new Order().by('name').thenBy('date');
	 * const activeOrders = order.getActiveOrders();
	 * // activeOrders: [{ propertyName: 'name', operator: ..., label: ... }, ...]
	 *
	 * @public
	 */
	public getActiveOrders(): OrderInfo[] {
		return this._orders
			.filter((order) => order.operator !== OrderOperator.IGNORE)
			.map((order) => ({ ...order })); // Restituisce copie per evitare mutazioni
	}
}

/**
 * Factory function per creare ordinamenti tipizzati
 * Compatibile con Vue 3 Composition API e completamente reattivo
 *
 * @returns Oggetto ordinamento tipizzato con API fluente
 *
 * @example
 * // Per Vue 3 Composition API
 * const dataOrder = ref(createOrderData().by('name', OrderOperator.ASCENDING));
 *
 * @example
 * // Con ordinamento multiplo
 * const complexOrder = ref(
 *   createOrderData()
 *     .by('priority', OrderOperator.DESCENDING)
 *     .thenBy('createdAt', OrderOperator.ASCENDING)
 *     .thenBy('name')
 * );
 */
export function createOrderData(): OrderFactory {
	// Oggetto combinato con dati e metodi
	const order: OrderFactory = {
		orders: [],

		by(propertyName: string, operator: OrderOperatorType = OrderOperator.ASCENDING): OrderFactory {
			order.orders = [
				{
					propertyName,
					operator,
				},
			];
			return order;
		},

		thenBy(
			propertyName: string,
			operator: OrderOperatorType = OrderOperator.ASCENDING,
		): OrderFactory {
			// Verifica se già esiste questo ordinamento
			const existingIndex = order.orders.findIndex((o) => o.propertyName === propertyName);
			if (existingIndex >= 0) {
				order.orders[existingIndex].operator = operator;
			} else {
				order.orders.push({
					propertyName,
					operator,
				});
			}
			return order;
		},

		changeOrder(propertyName: string, direction: string): void {
			const existingIndex = order.orders.findIndex((o) => o.propertyName === propertyName);

			// Mappa le direzioni Element UI agli operatori
			let newOperator: OrderOperatorType;
			if (direction === 'ascending') {
				newOperator = OrderOperator.ASCENDING;
			} else if (direction === 'descending') {
				newOperator = OrderOperator.DESCENDING;
			} else {
				newOperator = OrderOperator.IGNORE;
			}

			if (existingIndex >= 0) {
				if (newOperator === OrderOperator.IGNORE) {
					// Rimuove l'ordinamento
					order.orders.splice(existingIndex, 1);
				} else {
					// Aggiorna l'ordinamento esistente
					order.orders[existingIndex].operator = newOperator;
				}
			} else if (newOperator !== OrderOperator.IGNORE) {
				// Aggiunge nuovo ordinamento
				order.orders.push({
					propertyName,
					operator: newOperator,
				});
			}
		},

		clear(): OrderFactory {
			order.orders = [];
			return order;
		},

		remove(propertyName: string): OrderFactory {
			const index = order.orders.findIndex((o) => o.propertyName === propertyName);
			if (index >= 0) {
				order.orders.splice(index, 1);
			}
			return order;
		},

		formatAsText(): string {
			if (order.orders.length === 0) {
				return 'Nessun ordinamento';
			}

			return order.orders
				.filter((o) => o.operator !== OrderOperator.IGNORE)
				.map((o) => {
					const direction = o.operator === OrderOperator.ASCENDING ? '↑' : '↓';
					return `${o.propertyName} ${direction}`;
				})
				.join(', ');
		},

		hasOrders(): boolean {
			return order.orders.some((o) => o.operator !== OrderOperator.IGNORE);
		},
	};

	return order;
}
